import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CustomDate from "../../CustomForm/CustomDate";
import CustomInputSelect from "../../FormFields/CustomSelect/CustomInputSelect";
import CustomInput from "../../FormFields/CustomInput";
import CustomTimePicker from "../../FormFields/TiimePicker/TimePicker";

import { useMutation, useQuery } from "react-query";
import {
  getRecordAbsenceById,
  updateRecordAbsence,
} from "../../../api/services/recordAbsence";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { QK } from "../../../base/qk";

const RecordAbsenceEditModal = ({
  show,
  handleClose,
  selectedRecord,
  refetchRecord,
}) => {
  const { id: clientId } = useParams();

  const [formState, setFormState] = useState({
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    reason: "",
    absence_type: "",
    note_message: "",
    contact_information: "",
  });

  const [errors, setErrors] = useState({});

  const { isLoading: isFetching } = useQuery(
    [QK.record, selectedRecord],
    () => getRecordAbsenceById(selectedRecord),
    {
      enabled: !!selectedRecord,
      onSuccess: (data) => {
        setFormState({ ...data });
      },
    }
  );

  const editMutation = useMutation(
    ({ id, editData }) => updateRecordAbsence(id, editData),
    {
      onSuccess: () => {
        enqueueSnackbar("Record has been updated!", { variant: "success" });
        refetchRecord();
        handleClose();
      },
      onError: () => {
        enqueueSnackbar("Failed to update record", { variant: "error" });
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formState.start_date) newErrors.start_date = "Start date is required";
    if (!formState.end_date) newErrors.end_date = "End date is required";
    if (!formState.absence_type) newErrors.absence_type = "Absence type is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      enqueueSnackbar("Please fill in all required fields", { variant: "warning" });
      return;
    }
    
    const editData = {
      ...formState,
      client: clientId,
    };
    editMutation.mutate({ id: selectedRecord, editData });
  };

  const handleCloseModal = () => {
    setFormState({});
    setErrors({});
    handleClose();
  };

  const absenceOptions = [
    "Planned Absence",
    "Unplanned Absence",
    "Not Applicable",
  ];
  const reasonOptions = ["Sick", "Family Emergency", "Vacation", "Other"];

  const isPending = editMutation.isLoading || isFetching;

  return (
    <Dialog
      open={show}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <Box sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#EFF8FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditNoteIcon sx={{ color: "#2469F0", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Record Absence
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update absence record for the client
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Start Date & Time Section */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <PlayArrowIcon sx={{ color: "#10B981", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#10B981" }}>
                  Start of Absence
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    bgcolor: alpha("#10B981", 0.2),
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                  <CustomDate
                    label="Start Date *"
                    name="start_date"
                    value={formState.start_date}
                    onChange={handleChange}
                    error={!!errors.start_date}
                    helperText={errors.start_date}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                  <CustomTimePicker
                    label="Start Time"
                    name="start_time"
                    value={formState.start_time}
                    onChange={handleChange}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            {/* End Date & Time Section */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <StopIcon sx={{ color: "#EF4444", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#EF4444" }}>
                  End of Absence
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    bgcolor: alpha("#EF4444", 0.2),
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                  <CustomDate
                    label="End Date *"
                    name="end_date"
                    value={formState.end_date}
                    onChange={handleChange}
                    error={!!errors.end_date}
                    helperText={errors.end_date}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                  <CustomTimePicker
                    label="End Time"
                    name="end_time"
                    value={formState.end_time}
                    onChange={handleChange}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ opacity: 0.3 }} />

            {/* Details Section */}
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <CustomInputSelect
                  name="reason"
                  label="Reason"
                  selectedValue={formState.reason}
                  onChange={handleChange}
                  options={reasonOptions}
                  initialLabel
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <CustomInputSelect
                  name="absence_type"
                  label="Absence Type *"
                  selectedValue={formState.absence_type}
                  onChange={handleChange}
                  options={absenceOptions}
                  initialLabel
                  required
                  error={!!errors.absence_type}
                  helperText={errors.absence_type}
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <CustomInput
                label="Notes"
                name="note_message"
                value={formState.note_message}
                multiline
                rows={3}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            <Box>
              <CustomInput
                label="Contact Information"
                name="contact_information"
                multiline
                rows={2}
                value={formState.contact_information}
                onChange={handleChange}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            disabled={isPending}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 3,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {isPending ? "Updating..." : "Update Absence"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RecordAbsenceEditModal;